/**
 * ScheduleImpactPreviewModal
 * Generic modal for previewing the impact of contract actions (extension, rollover, capital amendment).
 * Shows summary comparison cards + full projected schedule using AmortizationScheduleTable.
 * Opened from action dialogs via "Preview Impact" button.
 */

import React, { useMemo } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import AmortizationScheduleTable from './AmortizationScheduleTable';
import ImpactSummaryCards from './ImpactSummaryCards';
import ChangeContextHeader from './ChangeContextHeader';
import ScheduleComparisonTable from './ScheduleComparisonTable';
import { buildComparisonRows } from '../../utils/scheduleComparison';

const ScheduleImpactPreviewModal = ({
  open,
  onClose,
  title = 'Schedule Impact Preview',
  subtitle,
  // Structured change context: array of { label, value }. Preferred over the
  // plain `subtitle` string so the header formats consistently across previews.
  changeContext,
  previewData,
  contract = {},
  contractOverrides = {},
  currencyCode,
  // Kind of change this preview represents (e.g. 'Capital Withdrawal',
  // 'Extension'); shown as a chip on impacted rows in the comparison table.
  changeTypeLabel = 'Change',
}) => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;

  // When the backend supplies the current schedule, render the shared
  // current-vs-proposed comparison table (consistent with the rate-change
  // preview). Falls back to the single-column projected table otherwise.
  // NOTE: hooks must run before any early return — guard on null previewData.
  const hasComparison = Array.isArray(previewData?.current_schedule) && previewData.current_schedule.length > 0;
  const comparisonRows = useMemo(
    () => (hasComparison
      ? buildComparisonRows(previewData.current_schedule, previewData.projected_schedule, {
        calcType: previewData.interest_calc_type,
        compoundingFrequency: previewData.compounding_frequency,
        dayCountBasis: previewData.day_count_basis,
        dp,
      })
      : []),
    [hasComparison, previewData, dp],
  );

  if (!previewData) return null;

  const displayContract = { ...contract, ...contractOverrides };
  const cc = currencyCode || displayContract?.currency_code || 'ZAR';

  const current = previewData.current || {};
  const projected = previewData.projected || {};
  const interestDiff = previewData.interest_diff || (projected.total_interest - current.total_interest) || 0;
  const percentageChange = current.total_interest
    ? (interestDiff / Math.abs(current.total_interest)) * 100
    : null;
  const isIncrease = interestDiff >= 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Change-context header */}
        <ChangeContextHeader lines={changeContext} subtitle={subtitle} />

        {/* Summary Comparison Cards (shared with the rate-change preview) */}
        <ImpactSummaryCards
          currentInterest={current.total_interest}
          proposedInterest={projected.total_interest}
          difference={interestDiff}
          percentageChange={percentageChange}
          currentWht={current.total_wht}
          proposedWht={projected.total_wht}
          currencyCode={cc}
          dp={dp}
          proposedLabel="Projected Total Interest"
          cardSize={3}
          extraCards={(
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Schedule Entries</Typography>
                <Typography variant="h6">
                  {current.entries || '-'} → {projected.entries || '-'}
                </Typography>
              </Box>
            </Grid>
          )}
        />

        {/* Comparison table when the current schedule is available; otherwise the
            single-column projected schedule (defensive fallback). */}
        {hasComparison ? (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Current vs Projected Schedule ({comparisonRows.length} entries)
            </Typography>
            <ScheduleComparisonTable
              rows={comparisonRows}
              isIncrease={isIncrease}
              dp={dp}
              currentLabel="Current"
              proposedLabel="Projected"
              changeTypeLabel={changeTypeLabel}
              currentVersion={previewData?.current_version ?? null}
              nextVersion={previewData?.next_version ?? null}
            />
          </Box>
        ) : (previewData.projected_schedule && previewData.projected_schedule.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Projected Schedule ({previewData.projected_schedule.length} entries)
            </Typography>
            <AmortizationScheduleTable
              schedule={previewData.projected_schedule}
              contract={displayContract}
              previewMode
              showAllRows
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleImpactPreviewModal;
